import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import {
  Search,
  Filter,
  ArrowUp,
  ArrowDown,
  Smile,
  Frown,
  Trophy,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../Components/UI/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/UI/table";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
} from "../../Components/UI/drawer";
import DrawerFilter from "../../Components/Common/DrawerFilter";
import { CommonFilter } from "../../Components/Common/CommonFilter";
import PeriodFilter from "../../Components/Common/PeriodFilter";
import { cn } from "../../Lib/Cn";
import { RiResetLeftFill } from "react-icons/ri";
import useIsMobile from "../../Hooks/useIsMobile";
import { getBiggestCustomersAPI } from "./ReportAPI";
import { checkAPIResponse } from "../../Utils/APIResponseUtils";
import {
  getPastMonthOptions,
  months,
  getDateRangeOptions,
  getPastQuarterOptions,
  getOtherYearOptions,
  getDateWithoutTime,
  formatAmount,
  FormatAmount,
} from "../../Utils/Utilities";
import { useSelector } from "react-redux";
import { TooltipProvider } from "../../Components/UI/tooltip";
import { useLocation } from "react-router-dom";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { useCurrency } from "../../Hooks/useCurrency";
import Pagination from "../../Components/Common/Pagination";


const legendStyle = `
  .recharts-legend-wrapper svg rect {
    rx: 4 !important;
    ry: 4 !important;
  }
  /* Force remove black focus boxes on click/focus */
  .recharts-surface:focus, 
  .recharts-layer:focus, 
  .recharts-wrapper:focus,
  .recharts-rectangle:focus {
    outline: none !important;
  }
  path:focus {
    outline: none !important;
  }
  *:focus {
    outline: none !important;
  }
`;

const themeColors = [
  "#0c6980",
  "#0d1f2d",
  "#0b5d1e",
  "#d4af37",
  "#bf5700",
  "#841617",
];

// --- ADD THIS NEW FUNCTION HERE ---
const renderStars = (score) => {
  const totalStars = 5;
  const filledStars = Math.round(score);

  const getStarColor = (s) => {
    if (s <= 1) return "#ef4444"; // Red
    if (s <= 2) return "#f97316"; // Orange
    if (s <= 3) return "#d4af37"; // Yellow
    return "#0b5d1e"; // Green
  };

  const activeColor = getStarColor(filledStars);

  return (
    <div className="flex items-center gap-0.5" title={`${score}/5`}>
      {[...Array(totalStars)].map((_, i) => (
        <svg
          key={i}
          viewBox="0 0 24 24"
          fill={i < filledStars ? activeColor : "#e5e7eb"}
          className="w-4 h-4"
        >
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      ))}
    </div>
  );
};

const CustomerAnalysis = () => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const currencySymbol = useCurrency();
  const { data: allConns = [] } = useSelector((state) => state.connectionData);
  const activeData = useSelector((state) => state.activeData);

  const monthlyJson = useMemo(() => {
    const raw = activeData.data.MonthlyThreads;
    // If it's a string, parse it; if it's already an array, use it; otherwise empty array
    if (typeof raw === 'string') return JSON.parse(raw);
    if (Array.isArray(raw)) return raw;
    return [];
  }, [activeData.data.MonthlyThreads]);

  const getTypeFromId = (id) => {
    if (id === "2") return "Happy";
    if (id === "3") return "Unhappy";
    return "Biggest";
  };

  const getFilterFromParams = () => {
    const searchParams = new URLSearchParams(location.search);
    const typeIdParam = searchParams.get("typeId") || "1";
    const customerType = getTypeFromId(typeIdParam);

    const dateRange = searchParams.get("p") || "This Month";

    const currentDate = new Date();
    const currentMonth = months[currentDate.getMonth()];
    const currentYear = currentDate.getFullYear();
    const defaultMonthKey = `${currentMonth} ${currentYear}`;

    const otherMonth = searchParams.get("om") || defaultMonthKey;
    const otherQuarter = searchParams.get("oq") || "all";
    const otherYear = searchParams.get("oy") || "all";

    const urlVType = searchParams.get("vType");
    const vType = urlVType === "2" ? "value" : "count";

    return {
      filters: { dateRange, otherMonth, otherQuarter, otherYear, customerType },
      standalone: { valueType: vType, typeId: typeIdParam },
    };
  };

  const initialParams = getFilterFromParams();

  // State
  const [valueType, setValueType] = useState(
    initialParams.standalone.valueType
  );
  const [customerType, setCustomerType] = useState(
    initialParams.filters.customerType
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [apiReportData, setApiReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Filter UI State
  const [isFilterSelected, setIsFilterSelected] = useState(!isMobile);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Change your initial state to point to totalValue Descending
  const [sortConfig, setSortConfig] = useState({
    key: "totalValue",
    direction: "desc",
  });


  // Pagination
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filters Data
  const [selectedFilters, setSelectedFilters] = useState(initialParams.filters);
  const [tempFilters, setTempFilters] = useState(initialParams.filters);
  const [canRenderSecondChart, setCanRenderSecondChart] = useState(false);
  const prevFiltersRef = useRef({});

  const currentDate = new Date();
  const currentMonth = months[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();
  const monthKey = `${currentMonth} ${currentYear}`;
  const oldestSinceDT = useMemo(() => {
    if (!allConns || allConns.length === 0) return new Date();

    const dates = allConns.map(conn => {
      const dateStr = conn.SinceDT || conn.CreatedDT;
      return dateStr ? new Date(dateStr) : null;
    }).filter(d => d !== null && !isNaN(d));

    if (dates.length === 0) return new Date();

    // Return the minimum (oldest) date
    return new Date(Math.min(...dates));
  }, [allConns]);
    const hasActiveFilters = useMemo(() => {
  const currentDate = new Date();
  const currentMonth = months[currentDate.getMonth()];
  const currentYear = currentDate.getFullYear();
  const defaultMonthKey = `${currentMonth} ${currentYear}`;

  return Object.entries(selectedFilters).some(([key, value]) => {
    // Ignore customerType as it's part of the page identity
    if (key === "customerType") return false;

    // Check if dateRange is different from default "This Month"
    if (key === "dateRange") {
      return value !== "This Month";
    }

    // Check otherMonth only if dateRange is "Other Month"
    if (key === "otherMonth") {
      return selectedFilters.dateRange === "Other Month" && value !== defaultMonthKey;
    }

    // Check otherQuarter only if dateRange is "Other Quarter"
    if (key === "otherQuarter") {
      return selectedFilters.dateRange === "Other Quarter" && value !== "all";
    }

    // Check otherYear only if dateRange is "Other Year"
    if (key === "otherYear") {
      return selectedFilters.dateRange === "Other Year" && value !== "all";
    }

    // All other filters
    return value !== "all";
  });
}, [selectedFilters]);

  const filterOptions = useMemo(() => [
    {
      key: "dateRange",
      label: "Period",
      options: getDateRangeOptions(),
      searchEnabled: true,
      type: "options",
      showAllOption: false,
    },
    {
      key: "otherMonth",
      label: "Select Month",
      options: getPastMonthOptions(oldestSinceDT),
      disabled: selectedFilters.dateRange !== "Other Month" && tempFilters.dateRange !== "Other Month",
      mobiledisabled: tempFilters.dateRange !== "Other Month",
      type: "list",
    },
    {
      key: "otherQuarter",
      label: "Select Quarter",
      options: getPastQuarterOptions(oldestSinceDT),
      disabled: selectedFilters.dateRange !== "Other Quarter" && tempFilters.dateRange !== "Other Quarter",
      mobiledisabled: tempFilters.dateRange !== "Other Quarter",
      type: "list",
    },
    {
      key: "otherYear",
      label: "Select Year",
      options: getOtherYearOptions(oldestSinceDT),
      disabled: !(selectedFilters.dateRange === "Other Year"),
      type: "list",
    },
  ], [oldestSinceDT, selectedFilters, tempFilters, monthKey]);

  useEffect(() => {
    document.title = `${customerType} Customers`;
    return () => {
      document.title = "SahiClub";
    }
  }, [customerType]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setCanRenderSecondChart(true);
    }, 400); // 400ms delay for the second graph
    return () => clearTimeout(timer);
  }, []);

  // Fetch Data
  const fetchCustomerData = async () => {
    try {
      const today = new Date();
      let prdType = "MM";
      let prdQty = 1;
      let year = today.getFullYear();
      let month = today.getMonth() + 1;

      const selectedRange = tempFilters.dateRange || "This Month";

      if (selectedRange === "Last Month") {
        month = month - 1;
        if (month === 0) {
          month = 12;
          year = year - 1;
        }
      } else if (selectedRange === "Other Month" && tempFilters.otherMonth) {
        const [mName, yStr] = tempFilters.otherMonth.split(" ");
        if (mName && yStr) {
          month = months.indexOf(mName) + 1;
          year = parseInt(yStr);
        }
      } else if (selectedRange === "This Quarter") {
        prdType = `QQ${Math.ceil(month / 3)}`;
        month = 0;
      } else if (
        selectedRange === "Other Quarter" &&
        tempFilters.otherQuarter &&
        tempFilters.otherQuarter !== "all"
      ) {
        const [qStr, yStr] = tempFilters.otherQuarter.split(" ");
        if (qStr && yStr) {
          prdType = `QQ${qStr.replace("Q", "")}`;
          year = parseInt(yStr);
          month = 0;
        }
      } else if (selectedRange === "This Year") {
        prdType = "YY";
        month = 0;
      } else if (
        selectedRange === "Other Year" &&
        tempFilters.otherYear &&
        tempFilters.otherYear !== "all"
      ) {
        prdType = "YY";
        year = parseInt(tempFilters.otherYear);
        month = 0;
      }

      // CHECK GUARD: If these exact DB parameters were already fetched, STOP.
      const prev = prevFiltersRef.current;
      if (
        prev.prdType === prdType &&
        prev.year === year &&
        prev.month === month &&
        prev.prdQty === prdQty &&
        apiReportData.length > 0
      ) {
        console.log("Skipping API call: Data already loaded for this period");
        return;
      }

      setIsLoading(true);

      const payload = [
        {
          threadTypeID: "2",
          prdType: prdType,
          prdQty: prdQty,
          year: year,
          month: month,
          changeType: "BIG",
        },
      ];

      const result = await getBiggestCustomersAPI(payload);
      checkAPIResponse(result);

      if (result?.updatePackage) {
        const updtPkg = JSON.parse(result.updatePackage);
        setApiReportData(updtPkg.Table1 || []);

        // UPDATE REF with the resolved API params to prevent future redundant calls
        prevFiltersRef.current = {
          prdType,
          year,
          month,
          prdQty,
        };
      } else {
        setApiReportData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setApiReportData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, [tempFilters]);

  useEffect(() => {
    const handleHeaderFilterClick = () => {
      setIsFilterSelected(true);
      setIsDrawerOpen(true);
    };
    window.addEventListener(
      "open-customer-analysis-filter",
      handleHeaderFilterClick
    );
    return () =>
      window.removeEventListener(
        "open-customer-analysis-filter",
        handleHeaderFilterClick
      );
  }, []);

  // Sync from URL and Set Active Filter State
  useEffect(() => {
    const params = getFilterFromParams();
    const currentType = params.filters.customerType;

    if (JSON.stringify(params.filters) !== JSON.stringify(selectedFilters)) {
      setCustomerType(params.filters.customerType);
      setSelectedFilters(params.filters);
      setTempFilters(params.filters);
    }

    // Set sorting based on type
    if (currentType === "Happy") {
      setSortConfig({ key: "fdbkScore", direction: "desc" });
    } else if (currentType === "Unhappy") {
      setSortConfig({ key: "fdbkScore", direction: "asc" });
    } else {
      setSortConfig({ key: "totalValue", direction: "desc" });
    }

    if (params.standalone.valueType !== valueType) {
      setValueType(params.standalone.valueType);
    }
  }, [location.search]); // Removed searchTerm dependency as requested
  // Sync to URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set("vType", valueType === "value" ? "2" : "1");
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    if (window.location.search !== `?${params.toString()}`) {
      window.history.replaceState({}, "", newUrl);
    }
  }, [valueType]);

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const rawMappedData = useMemo(() => {
    // Create a map of API data for quick lookup
    const apiDataMap = new Map(apiReportData.map(item => [String(item.ConnID), item]));

    // Start with ALL connections from Redux to ensure those with 0 orders but high/low feedback show up
    return allConns.map((conn) => {
      const apiItem = apiDataMap.get(String(conn.ConnID));

      return {
        ConnID: conn.ConnID,
        customerName: conn.BizName || conn.ContactName || `Customer #${conn.ConnID}`,
        lastOrderDate: apiItem?.LastThrDT || null,
        threadCount: apiItem?.CntThread || 0,
        totalValue: apiItem?.BizProspect || 0,
        fdbkScore: conn.FdbkScore || 0,
        FdbkNotes: conn.FdbkNotes || "",
        isOthers: false
      };
    });
  }, [apiReportData, allConns]);

  const finalData = useMemo(() => {
    let data = [...rawMappedData];

    // Filter by Customer Type
    if (customerType === "Happy") {
      // Show those with high scores even if threadCount is 0
      data = data.filter((i) => i.fdbkScore > 3);
    } else if (customerType === "Unhappy") {
      // Show those with low scores even if threadCount is 0
      data = data.filter((i) => i.fdbkScore > 0 && i.fdbkScore <= 3);
    } else {
      // For "Biggest", usually we only care about people who actually have orders
      data = data.filter(i => i.threadCount > 0 || i.totalValue > 0);
    }

    // Search Filter
    if (searchTerm) {
      data = data.filter((i) =>
        i.customerName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Biggest Customers "Others" aggregation (only for Biggest type)
    if (customerType === "Biggest" && data.length > 10 && !searchTerm) {
      data.sort((a, b) => (b.totalValue || 0) - (a.totalValue || 0));
      const top10 = data.slice(0, 10);
      const othersRaw = data.slice(10);

      const othersAggregated = {
        ConnID: "others-aggregated",
        customerName: "Others",
        lastOrderDate: othersRaw.reduce((latest, item) => {
          const current = new Date(item.lastOrderDate);
          return (!latest || (item.lastOrderDate && current > new Date(latest))) ? item.lastOrderDate : latest;
        }, null),
        threadCount: othersRaw.reduce((sum, i) => sum + i.threadCount, 0),
        totalValue: othersRaw.reduce((sum, i) => sum + i.totalValue, 0),
        fdbkScore: 0,
        isOthers: true
      };
      return [...top10, othersAggregated];
    }

    return data;
  }, [rawMappedData, searchTerm, customerType]);

  // --- UPDATED CHART DATA ---

  const ordersChartData = useMemo(() => {
    // 1. Get individual top customers
    const individuals = finalData
      .filter(item => !item.isOthers && item.threadCount > 0);

    // 2. Find the Others row
    const othersRow = finalData.find(item => item.isOthers);
    const top5 = individuals.sort((a, b) => b.threadCount - a.threadCount).slice(0, 5);

    // 3. Combine and Sort: Show Top 5 individuals + Others, 
    // then sort the result so the LONGEST bar is always at the top
    const combined = othersRow ? [...top5, othersRow] : top5;
    return combined.sort((a, b) => b.threadCount - a.threadCount);
  }, [finalData]);



  const valueChartData = useMemo(() => {
    const individuals = finalData.filter(item => !item.isOthers && item.totalValue > 0);
    const othersRow = finalData.find(item => item.isOthers);

    // Take top 5 individuals
    const top5 = individuals.sort((a, b) => b.totalValue - a.totalValue).slice(0, 5);

    // Combine top 5 + Others
    const combined = othersRow ? [...top5, othersRow] : top5;
    return combined.sort((a, b) => b.totalValue - a.totalValue);
  }, [finalData]);

  // Chart Data - Force independent sorting for the Top 5 logic
  const chartData = useMemo(() => {
    const metric = valueType === "count" ? "OrderCount" : "totalValue";

    return [...finalData]
      .filter((item) => (item[metric] || 0) > 0) // Only show items with data
      .sort((a, b) => (b[metric] || 0) - (a[metric] || 0)) // Always High to Low
      .slice(0, 5); // Take top 5
  }, [finalData, valueType]);

  const shouldShowCharts = customerType === "Biggest" && finalData.length > 1;
  const totalCount = finalData.reduce((s, i) => s + i.threadCount, 0);
  const totalValue = finalData.reduce((s, i) => s + i.totalValue, 0);

  const currentTableData = useMemo(() => {
    // 1. Separate "Others" from individual customers
    let individuals = finalData.filter(item => !item.isOthers);
    const othersRow = finalData.find(item => item.isOthers);

    // 2. Sort ONLY the individual customers
    if (sortConfig.key) {
      individuals.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === "customerName") {
          return sortConfig.direction === "asc"
            ? (aVal || "").localeCompare(bVal || "")
            : (bVal || "").localeCompare(aVal || "");
        }

        if (sortConfig.key === "lastOrderDate") {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        } else {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }

        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      });
    }

    // 3. Handle Pagination/Mobile view
    if (isMobile) {
      // On mobile, show sorted individuals + others at the end
      return othersRow ? [...individuals, othersRow] : individuals;
    }

    // On Desktop, slice the individuals, THEN append "Others"
    const start = (currentPage - 1) * rowsPerPage;
    const slicedIndividuals = individuals.slice(start, start + rowsPerPage);

    if (customerType === "Biggest") {
      // For Biggest, return the top 10 individuals + others row (No Pagination)
      return othersRow ? [...individuals, othersRow] : individuals;
    } else {
      // For Happy/Unhappy, return paginated individuals
      const start = (currentPage - 1) * rowsPerPage;
      return individuals.slice(start, start + rowsPerPage);
    }
  }, [finalData, sortConfig, currentPage, rowsPerPage, isMobile, customerType]);

  const handleApplyFilters = () => {
    setSelectedFilters(tempFilters);
    setIsDrawerOpen(false);
    setIsFilterSelected(false);
    setSearchTerm("");
  };

  const handleCancelFilters = () => {
    setTempFilters(selectedFilters);
    setIsDrawerOpen(false);
    setIsFilterSelected(false);
  };

  const handleResetFilters = () => {
    const reset = {
      dateRange: "This Month",
      otherMonth: monthKey,
      otherQuarter: "all",
      otherYear: "all",
      customerType: customerType,
    };
    setTempFilters(reset);
    setSelectedFilters(reset);
    setValueType("count");
    setSearchTerm("");
  };
  const handleSort = (key) => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key;
      return {
        key,
        // If clicking a new column, default to desc. If same column, toggle.
        direction: isSameKey && prev.direction === "desc" ? "asc" : "desc",
      };
    });
  };
  const renderEmptyState = () => {
    let config = {
      icon: Trophy,
      description:
        "Identify your highest value customers by revenue and order volume.",
      subText: "No data available right now.",
    };

    if (customerType === "Happy") {
      config = {
        icon: Smile,
        description:
          "Track most satisfied customers based on feedback and interactions.",
        subText: "No data available right now.",
      };
    } else if (customerType === "Unhappy") {
      config = {
        icon: Frown,
        description:
          "Identify dissatisfied customers needing immediate attention.",
        subText: "No data available right now.",
      };
    } else if (customerType === "Biggest") {
      config = {
        icon: TrendingUp,
        description:
          "Identify your highest value customers by revenue and order volume.",
        subText: "No data available right now.",
      };
    }

    const IconComponent = config.icon;
    const handleSort = (key) => {
      setSortConfig((prev) => {
        const isSameKey = prev.key === key;
        return {
          key,
          // If clicking new column, default to desc. If same column, toggle.
          direction: isSameKey && prev.direction === "desc" ? "asc" : "desc",
        };
      });
    };
    return (
      <div className="w-full">
        <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-white shadow-sm w-full md:w-fit md:min-w-[500px]">
          <div className="flex-shrink-0 p-2 bg-gray-50 rounded-md">
            <IconComponent
              className="h-6 w-6 text-gray-400"
              strokeWidth={2}
            />
          </div>
          <div className="flex flex-col gap-1">
            {/* Font sizes: Adjusted for better mobile hierarchy */}
            <span className="text-sm font-semibold text-gray-700 leading-tight">
              {config.description}
            </span>
            <span className="text-xs font-medium text-gray-400">
              {config.subText}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const getTypeIcon = () => {
    if (customerType === "Happy")
      return <Smile className="inline mb-1 ml-2 text-sahi-green" size={24} />;
    if (customerType === "Unhappy")
      return <Frown className="inline mb-1 ml-2 text-sahi-red" size={24} />;
    return <Trophy className="inline mb-1 ml-2 text-sahi-yellow" size={24} />;
  };

  const getSentimentEmoji = (score) => {
    if (score > 3) return <Smile className="inline text-green-600" size={18} />;
    return <Frown className="inline text-red-600" size={18} />;
  };

  // --- UPDATE THIS COMPONENT ---
  const MobileCard = ({ item }) => {
    return (
      <div className="px-3 w-full">
        <div className="bg-sahi-white sahi-radius shadow-sm border border-gray-200 mb-2 w-full flex flex-col justify-between">
          {/* Header: Name + Star Rating */}
          <div className="flex justify-between items-center p-3 pb-2 px-4">
            <div className="flex flex-col min-w-0 pr-2">
              <p className="font-semibold text-gray-900 text-sm truncate max-w-[220px]">
                {item.customerName}
              </p>
            </div>

            {/* Change: Only show stars if NOT Biggest Customer */}
            {customerType !== "Biggest" && (
              <div className="flex-shrink-0">
                {/* This replaces the emoji with your star function */}
                {renderStars(item.fdbkScore)}
              </div>
            )}
          </div>

          {/* Footer: Date + Metric (Amount/Ords) */}
          <div className="flex justify-between items-center text-xs px-4 pb-3">
            <div className="flex items-center gap-1.5 text-gray-500">
              <span> Last Sale: </span>
              <span>{getDateWithoutTime(item.lastOrderDate)}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-semibold text-sahi-primary-color">
                {valueType === "count"
                  ? `${item.threadCount} ${item.threadCount > 1 ? "Sales" : "Sale"}`
                  : `${currencySymbol} ${formatAmount(item.totalValue)}`}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ReportTable = () => {
    const isBiggest = customerType === "Biggest";

    const renderSortArrow = (key) => {
      const isBiggest = customerType === "Biggest";
      const sortableKeys = isBiggest ? ["totalValue"] : ["fdbkScore"];

      if (!sortableKeys.includes(key)) return null;

      const isActive = sortConfig.key === key;
      if (!isActive)
        return (
          <ArrowDown className="ml-1 h-3 w-3 text-gray-300 opacity-50 group-hover:opacity-100 transition-opacity" />
        );

      return sortConfig.direction === "desc" ? (
        <ArrowDown className="ml-1 h-4 w-4 text-black" />
      ) : (
        <ArrowUp className="ml-1 h-4 w-4 text-black" />
      );
    };

    return (
      <div className="overflow-x-auto">
        <Table className="min-w-full table-fixed border-collapse">
          <TableHeader className="bg-gray-50/50">
            {/* FIX 2: Change bg-sahi-white to transparent or gray-50/50 */}
            <TableRow className="bg-transparent border-b-2 border-gray-200">
              <TableHead className="h-11 font-semibold text-sm text-black px-4 align-middle text-left w-[220px]">
                Customer
              </TableHead>

              {/* Inside TableHeader > TableRow */}
              {!isBiggest && (
                <>
                  <TableHead
                    className="h-11 font-semibold text-sm text-black px-2 align-middle text-left w-[120px] cursor-pointer group"
                    onClick={() => handleSort("fdbkScore")}
                  >
                    <div className="flex items-center">
                      Rating {renderSortArrow("fdbkScore")}
                    </div>
                  </TableHead>
                  {/* ALIGNMENT FIX: Changed px-2 to px-4 and added text-left */}
                  <TableHead className="h-11 font-semibold text-sm text-black px-4 align-middle text-left w-[300px]">
                    Feedback
                  </TableHead>
                </>
              )}
              <TableHead className="h-11 font-semibold text-sm text-black px-2 align-middle text-left w-[110px]">
                Last Sale
              </TableHead>

              <TableHead className="h-11 font-semibold text-sm text-black px-4 align-middle text-right w-[110px]">
                Sales
              </TableHead>

              {isBiggest && (
                <TableHead className="h-11 font-semibold text-sm text-black px-2 align-middle text-right w-[80px]">
                  Share
                </TableHead>
              )}

              {/* Value Header - The only one with Sort Logic now */}
              <TableHead
                className={cn(
                  "h-11 font-semibold text-sm text-black px-4 align-middle text-right w-[160px] group",
                  isBiggest ? "cursor-pointer" : "cursor-default"
                )}
                onClick={() => isBiggest && handleSort("totalValue")}
              >
                <div className="flex items-center justify-end">
                  Value ({currencySymbol}) {renderSortArrow("totalValue")}
                </div>
              </TableHead>

              {isBiggest && (
                <TableHead className="h-11 font-semibold text-sm text-black px-4 align-middle text-right w-[100px]">
                  Share
                </TableHead>
              )}
            </TableRow>
          </TableHeader>

          <TableBody>
            {currentTableData.map((item, idx) => (
              // Inside ReportTable map:
              <TableRow
                key={idx}
                className={cn(
                  "hover:bg-gray-50 border-b border-gray-200 transition-colors",
                  item.isOthers && "hover:bg-gray-50 border-b border-gray-200 transition-colors"
                )}
              >
                <TableCell className="px-4 py-2.5 text-left font-medium truncate max-w-[220px] text-sm text-text-color">
                  {item.customerName}
                </TableCell>

                {!isBiggest && (
                  <>
                    <TableCell className="px-2 py-2.5 text-left">
                      {renderStars(item.fdbkScore)}
                    </TableCell>
                    {/* New Feedback Column Data */}
                    <TableCell className="px-4 py-2.5 text-left font-medium truncate max-w-[220px] text-sm text-text-color" title={item.FdbkNotes}>
                      {item.FdbkNotes || <span >-</span>}
                    </TableCell>
                  </>
                )}

                <TableCell className="px-2 py-2.5 text-left whitespace-nowrap text-sm text-text-color">
                  {getDateWithoutTime(item.lastOrderDate)}
                </TableCell>

                <TableCell className="px-4 py-2.5 text-right text-sm text-text-color">
                  {item.threadCount.toLocaleString()}
                </TableCell>

                {isBiggest && (
                  <TableCell className="px-2 py-2.5 text-right text-sm text-text-color">
                    {(totalCount > 0 ? (item.threadCount / totalCount) * 100 : 0).toFixed(2)}%
                  </TableCell>
                )}

                <TableCell className="px-4 py-2.5 text-right text-sm text-text-color">
                  <FormatAmount
                    amount={item.totalValue || 0}
                    currency={currencySymbol}
                    showCurr={false}
                  />
                </TableCell>

                {isBiggest && (
                  <TableCell className="px-4 py-2.5 text-right text-sm text-text-color">
                    {(totalValue > 0 ? (item.totalValue / totalValue) * 100 : 0).toFixed(2)}%
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>

          <tfoot className="bg-gray-50/80 border-t border-gray-200">
            <TableRow className="h-11 hover:bg-transparent">
              <TableCell className="px-4 text-left text-black font-semibold text-sm align-middle">
                Total
              </TableCell>
              {!isBiggest && (
                <>
                  <TableCell className="px-2" />
                  <TableCell className="px-2" /> {/* Empty cell for Feedback column */}
                </>
              )}
              <TableCell className="px-2" />
              <TableCell className="px-4 text-right text-black font-semibold text-sm align-middle">
                {totalCount.toLocaleString()}
              </TableCell>
              {isBiggest && <TableCell className="px-2 text-right text-black font-semibold text-sm align-middle">100.00%</TableCell>}
              <TableCell className="px-4 text-right text-black font-semibold text-sm align-middle">
                <FormatAmount amount={totalValue || 0} currency={currencySymbol} showCurr={false} />
              </TableCell>
              {isBiggest && <TableCell className="px-4 text-right text-black font-semibold text-sm align-middle">100.00%</TableCell>}
            </TableRow>
          </tfoot>
        </Table>
      </div>
    );
  };

  const renderHorizontalBarChart = (data, dataKey) => {
    const formatRightLabel = (value) => {
      if (dataKey === "totalValue") return formatAmount(value);
      return value;
    };

    const dynamicHeight = Math.max(data.length * 45 + 20, 100);
    const maxValue = Math.max(...data.map((item) => item[dataKey] || 0), 1);

    return (
      <div style={{ width: '100%', height: `${dynamicHeight}px` }}>
        <ResponsiveContainer width="100%" height="100%">          <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            horizontal={false}
            stroke="#f0f0f0"
          />
          <XAxis type="number" hide domain={[0, maxValue * 1.2]} />
          <YAxis
            dataKey="customerName"
            type="category"
            width={100}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11, fill: "#6b7280", fontWeight: 500 }}
            tickFormatter={(v) =>
              v.length > 15 ? `${v.substring(0, 15)}...` : v
            }
          />
          <RechartsTooltip
            cursor={{ fill: "#f3f4f6", radius: 4 }}
            contentStyle={{
              borderRadius: "8px",
              border: "none",
              boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
            }}
            // Update the formatter to handle custom labels for both keys
            formatter={(value, name) => {
              let label = name;
              if (name === "threadCount" || name === "OrderCount") label = "Sales";
              if (name === "totalValue") label = "Value";

              const displayValue = name === "totalValue"
                ? `${currencySymbol}${formatAmount(value)}`
                : value.toLocaleString();

              return [displayValue, label];
            }}
          />
          <Bar
            dataKey={dataKey}
            barSize={20}
            radius={[0, 0, 0, 0]}
            animationDuration={1000}
            style={{ outline: 'none' }}
          >
            <LabelList
              dataKey={dataKey}
              position="right"
              formatter={formatRightLabel}
              style={{ fontSize: "9px", fontWeight: "bold", fill: "#374151" }}
            />
            {data.map((row, i) => (
              <Cell
                key={i}
                fill={
                  row.isOthers
                    ? "#94a3b8"
                    : themeColors[i % themeColors.length] // Removed the > 0 check here
                }
              />
            ))}
          </Bar>
        </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <style>{legendStyle}</style>
      <div className="lg:space-y-6 overflow-x-hidden pb-4">
        {/* ----------------- Header Section (Desktop Only) ----------------- */}
        <div className="hidden lg:flex justify-between items-center gap-3 h-12">
          <div className="flex items-center flex-grow">
            <h1 className="text-3xl font-bold text-text-color mr-8 web-header-name flex items-center">
              {customerType} Customers
            </h1>

            {/* <div className="flex items-center justify-end flex-grow gap-3">
                           
                                    <div className={cn("flex items-center px-3 lg:px-4 h-8 lg:h-10", "w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm")}>
                                        <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                                            <span>Count:</span>
                                            <span className="font-medium">{totalCount}</span>
                                        </span>
                                    </div>
                                    <div className={cn("flex items-center px-3 lg:px-4 h-8 lg:h-10", "w-fit max-w-[240px] bg-white text-black rounded-lg border filter-border shadow-sm")}>
                                        <span className="f-row gap-1 whitespace-nowrap text-xs lg:text-sm">
                                            <span>Value:</span>
                                             <span className="font-medium">{currencySymbol}{totalValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                        </span>
                                    </div>
*/}
            {/* Search and Summary Badges */}
            <div className="flex items-center justify-end flex-grow gap-3">
              {(finalData.length > 11 || searchTerm) && (
                <div className="relative w-120 filter-border">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                  <Input
                    placeholder="Search Customer"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none"
                  />
                </div>
              )}
            </div>
          </div>

          {/* <div className="flex items-center gap-3 h-full">
                       
                            <Button
                                className={cn("button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px] relative", isFilterSelected && "filter-active")}
                                onClick={() => setIsFilterSelected(prev => !prev)}
                            >
                                <div className="h-6 w-6 flex items-center justify-center transition-all">
                                    <Filter className={cn("h-4 w-4", isFilterSelected ? "text-sahi-primary-color" : "text-white group-hover:text-sahi-primary-color")} />
                                </div>
                            </Button>
                       
                    </div> */}
        </div>

        {/* ----------------- Filter Bar (Desktop) ----------------- */}
        {isFilterSelected && !isMobile && customerType === "Biggest" && (
          <div className="hidden lg:flex flex-wrap gap-4 items-center mb-2 animate-in fade-in duration-300">
            {/* Period Filter Component */}
            <PeriodFilter
              value={selectedFilters}
              onChange={(newFilters) => {
                setSelectedFilters(newFilters);
                setTempFilters(newFilters);
              }}
              monthlyData={monthlyJson}
              showMonthFilter={true}
              showQuarterFilter={true}
              showYearFilter={true}
            />
          </div>
        )}

        {/* ----------------- Filter Drawer (Mobile) ----------------- */}
        {isFilterSelected && isMobile && (
          <Drawer
            open={isDrawerOpen}
            onOpenChange={(open) => {
              setIsDrawerOpen(open);
              if (!open) setIsFilterSelected(false);
            }}
          >
            <DrawerContent className="p-0 [&>div:first-child]:hidden">
              <div className="f-col h-[100dvh] !rounded-t-[10px]">
                <div className="flex plain items-center justify-between p-4 border-b-2 border-gray-200 text-xl !rounded-t-[10px]">
                  <p className="text-lg font-semibold text-sahi-primary-color">
                    Filters & Sorting
                  </p>
                  <button
                    onClick={handleResetFilters}
                    className="group flex items-center gap-1 text-sm font-light hover:underline transition duration-200 text-sahi-primary-color"
                  >
                    <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform" />
                    <span>Reset</span>
                  </button>
                </div>
                <div className="flex flex-1 overflow-y-auto">
                  <div className="flex-1 p-3 overflow-y-auto filter-scroll-container">
                    {filterOptions.map((opt) => (
                      <div key={opt.key} id={opt.key} className="mb-4">
                        <DrawerFilter
                          id={opt.key}
                          label={opt.label}
                          options={opt.options}
                          value={tempFilters[opt.key]}
                          onChange={(value) => {
                            setTempFilters((prev) => ({
                              ...prev,
                              [opt.key]: value,
                            }));
                          }}
                          disabled={opt.mobiledisabled || opt.disabled}
                          type={opt.type || "list"}
                          mobileDropdownText={opt.mobileDropdownText}
                          MobileLabel={opt.MobileLabel}
                          showAllOption={opt.showAllOption}
                          mobileNoLabel={opt.mobileNoLabel}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-bg-white">
                  <DrawerClose asChild>
                    <div className="f-row justify-start gap-2">
                      <Button
                        className="sahi-primary-button"
                        onClick={handleApplyFilters}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        className="sahi-secondary-button"
                        onClick={handleCancelFilters}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        )}

        {/* ----------------- Main Content Area ----------------- */}

        {/* {finalData.length === 0 && (
                    <div className="f-col items-start gap-5 mt-0">
                        <p className="no-records border border-gray-200 shadow-sm sahi-radius">
                            <span>No data found{(hasActiveFilters || searchTerm) ? ' for selected filters.' : '.'}</span>
                            {(hasActiveFilters || searchTerm) && (
                                <span onClick={handleResetFilters} className="text-sahi-primary-color underline hover:text-sahi-primary-color/80 cursor-pointer ml-2">
                                    Clear filter.
                                </span>
                            )}
                        </p>
                    </div>
                )} */}
{finalData.length === 0 && !isLoading && (
  /* FIX: Changed lg:pt-2 to lg:pt-0 to move it to the absolute top of its container */
  <div className={cn("flex flex-col items-start  w-full lg:pt-0", isMobile ? "px-3 pt-3" : "px-0 pt-5")}>
    {hasActiveFilters ? (
      /* FIX: Added lg:pt-0 and lg:px-0 to remove all spacing on desktop */
      <div className="f-col items-start gap-5 mt-0 pt-5 lg:pt-0  lg:px-0 w-full">
        <div className="no-records border border-gray-200 shadow-sm sahi-radius p-4 bg-white flex items-center w-full md:w-fit">
          <span >
            No records found for the selected filters.
          </span>
          <button
            onClick={handleResetFilters}
                    className="text-sahi-primary-color underline hover:text-sahi-primary-color/80 cursor-pointer ml-2"
          >
            Clear filter.
          </button>
        </div>
      </div>
    ) : (
      /* Moves renderEmptyState up as well */
      <div className="lg:mt-0">
        {renderEmptyState()}
      </div>
    )}
  </div>
)}
        {finalData.length > 0 && (
          <div
            className={cn(
              "grid grid-cols-1 gap-4 w-full items-start",
              shouldShowCharts && !isMobile
                ? "lg:grid-cols-[1fr_700px]"
                : "lg:grid-cols-1"
            )}
          >
            {/* ---------------- Mobile View ---------------- */}
            {isMobile && (
              <>
                <div className="sticky top-0 z-20 bg-white shadow-sm">
                  {/* Mobile Toggle Tabs */}
                  <div className="flex w-full border-b border-gray-200 bg-white">
                    <button
                      onClick={() => setValueType("count")}
                      className={cn(
                        "flex-1 py-2.5 text-sm font-medium transition-all relative",
                        valueType === "count"
                          ? "text-sahi-primary-color border-b-2 border-sahi-green"
                          : "text-gray-500 hover:text-gray-700"
                      )}
                    >
                      {customerType === "Biggest" ? "Sales" : "Sales"}
                    </button>

                    <button
                      onClick={() => setValueType("value")}
                      className={cn(
                        "flex-1 py-2.5 text-sm font-medium transition-all relative",
                        valueType === "value"
                          ? "text-sahi-primary-color border-b-2 border-sahi-green"
                          : "text-gray-500 hover:text-gray-700"
                      )}
                    >
                      {customerType === "Biggest" ? "Value" : "Amount"}
                    </button>
                  </div>


                </div>
                {customerType !== "Biggest" && (finalData.length > 10 || searchTerm) && (
                  <div className="px-3 -mt-1">
                    <div className="relative w-full">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search Customer"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 h-10 w-full bg-gray-50 border-gray-200 focus:ring-1 focus:ring-sahi-primary-color"
                      />
                    </div>
                  </div>
                )}

                {/* Mobile Chart */}
                {shouldShowCharts && (
                  <div className="w-[calc(100%+2rem)] -ml-4 mb-0">
                    <Card className="bg-white border-y border-gray-200 border-x-0 shadow-none rounded-none">
                      {/* Mobile Chart CardContent */}
                      <CardContent className="p-1.5 h-auto"> {/* Changed from min-h-[250px] to h-auto */}
                        <div className="w-full h-auto">
                          {valueType === "count"
                            ? renderHorizontalBarChart(ordersChartData, "threadCount")
                            : renderHorizontalBarChart(valueChartData, "totalValue")}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Mobile List View */}
                <div className="w-[calc(100%+2rem)] -ml-4 mt-0">
                  <div className=" px-4 -mt-1 pb-20">
                    {currentTableData.map((item, idx) => (
                      <MobileCard key={idx} item={item} />
                    ))}
                  </div>

                  {/* Mobile Total Row */}
                  <div className="fixed bottom-0 left-0 w-full bg-gray-50 border-t border-gray-300 z-10 px-4 py-2">
                    <div className="flex justify-between items-center h-full">
                      <div className="text-sm text-black font-semibold">
                        Total
                      </div>
                      <div className="flex gap-4">
                        {valueType === "count" ? (
                          <div className="text-right text-sm text-black font-semibold">
                            {totalCount.toLocaleString()} <span className="text-xs font-normal text-gray-500">(100%)</span>
                          </div>
                        ) : (
                          <div className="text-right text-sm text-black font-semibold">
                            {formatAmount(totalValue)} <span className="text-xs font-normal text-gray-500">(100%)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ---------------- Desktop View ---------------- */}
            {!isMobile && (
              <>
                {/* Left Column: Table */}
                <div className="flex flex-col">
                  <Card className="bg-sahi-white !pt-0 overflow-hidden border border-gray-200 shadow-sm h-fit">
                    <CardContent className="px-0 py-0 !sahi-radius">
                      <ReportTable />
                    </CardContent>
                  </Card>
                  {customerType !== "Biggest" && finalData.length > rowsPerPage && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(finalData.length / rowsPerPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(size) => setRowsPerPage(size)}
                      onPageChange={(page) => setCurrentPage(page)}
                    />
                  )}
                </div>

                {/* Right Column: Charts (Conditional) */}

                {shouldShowCharts && (
                  <div className="flex flex-col gap-4">
                    <Card className="bg-white border border-gray-200 shadow-sm rounded-lg">
                      <CardHeader className=" border-b">
                        <CardTitle className="text-sm font-semibold text-center text-gray-700">
                          Sales Distribution
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-1 h-auto">
                        {/* FIX 1: Changed valueChartData to ordersChartData and dataKey to threadCount */}
                        {canRenderSecondChart ? (
                          renderHorizontalBarChart(ordersChartData, "threadCount")
                        ) : (
                          <div className="h-[150px] w-full flex items-center justify-center text-gray-400 text-xs">
                            Loading distribution...
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Value Distribution Chart */}
                    <Card className="bg-white border border-gray-200 shadow-sm rounded-lg">
                      <CardHeader className=" border-b ">
                        <CardTitle className="text-sm font-semibold text-center text-gray-700">
                          Value Distribution
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-1 h-auto">
                        {canRenderSecondChart ? (
                          renderHorizontalBarChart(valueChartData, "totalValue")
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-gray-400 text-xs">
                            Loading distribution...
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </TooltipProvider >
  );
};

export default CustomerAnalysis;
